/** Background Singers (owner session cookie): leads, harmonies from your voice, AI choir, mixer, save / download / send. */
import axios from "axios";
import { API } from "@/lib/creatorApi";

const http = axios.create({ baseURL: `${API}/bgv`, withCredentials: true });
const data = (p) => p.then((r) => r.data);

export const BGV_ACTIVE = ["preparing", "rendering", "saving"];
export const PHASES = {
  queued: "Getting ready", loading: "Loading your vocal", separating: "Splitting your vocal from the music",
  analysing: "Listening to your voice (key, range, every note)", sections: "Finding the choruses",
  harmonies: "Making the harmonies", choir_cut: "Fitting the AI choir to your sections", mixing: "Mixing everything together",
  saving: "Saving the new song", ready: "Ready", error: "Stopped",
};
export const PREP_STEPS = ["loading", "analysing", "sections", "harmonies"];
export const CHOIR_PHASES = {
  asking: "Asking the AI choir to sing", singing: "The AI choir is singing over your music (1–3 minutes)",
  separating: "Taking the choir out of the AI's mix", lining_up: "Lining the choir up with your song",
};
export const phaseText = (p) => {
  const m = /^parts:(\d+)\/(\d+)$/.exec(p || "");
  return m ? `Making the harmonies (${m[1]} of ${m[2]} done)` : PHASES[p] || "Working";
};
export const HOW = [["harmony", "From my voice"], ["choir", "AI choir"], ["both", "Both"]];
export const VOICES = [["male", "Male"], ["female", "Female"], ["mixed", "Mixed"], ["choir", "Full gospel choir"]];
export const INTERVALS = [
  ["third_up", "3rd above"], ["fifth_up", "5th above"], ["octave_up", "Octave above"], ["unison", "Unison (double)"],
  ["third_down", "3rd below"], ["fourth_down", "4th below"], ["sixth_down", "6th below"], ["octave_down", "Octave below"],
];
export const ROLES = { bgva: "Alto", bgvb: "Soprano", bgvc: "Tenor", bgvd: "Bass" };
export const COUNTS = [[1, "Simple 2-part"], [2, "2 harmonies"], [3, "3-part"], [4, "4-part choir"]];
export const COLORS = { lead: "#f8fafc", bgva: "#f0abfc", bgvb: "#c4b5fd", bgvc: "#67e8f9", bgvd: "#6ee7b7", choir: "#fcd34d", music: "#94a3b8" };
export const usesHarmony = (s) => s.how !== "choir";
export const usesChoir = (s) => s.how !== "harmony";

export const listBgvSources = (q) => data(http.get("/sources", { params: q ? { q } : {} }));
export const createBgv = (body) => data(http.post("", body));
export const listBgv = (songId) => data(http.get("", { params: songId ? { song_id: songId } : {} }));
export const getBgv = (id) => data(http.get(`/${id}`));
export const saveBgvSettings = (id, settings) => data(http.put(`/${id}/settings`, settings));
export const renderBgv = (id) => data(http.post(`/${id}/render`));
export const retryBgv = (id) => data(http.post(`/${id}/retry`));
export const makeBgvChoir = (id) => data(http.post(`/${id}/choir`));
export const saveBgvSong = (id) => data(http.post(`/${id}/save`));
export const sendBgvToStudio = (id) => data(http.post(`/${id}/send`, {}));
export const deleteBgv = (id) => data(http.delete(`/${id}`));
export const partsZipUrl = (id) => `${API}/bgv/${id}/parts.zip`;
export const partWavUrl = (id, name) => `${API}/bgv/${id}/audio/${name}.wav?download=true`;

/** The mixer strip (level / pan / reverb / mute / solo) of one track. */
export const stripOf = (settings, name) => (name.startsWith("bgv") ? settings.parts.find((p) => p.name === name) : settings[name]);
export const withStrip = (settings, name, patch) => (name.startsWith("bgv")
  ? { ...settings, parts: settings.parts.map((p) => (p.name === name ? { ...p, ...patch } : p)) }
  : { ...settings, [name]: { ...settings[name], ...patch } });
export const audible = (settings, names) => {
  const solo = names.filter((n) => stripOf(settings, n)?.solo);
  return solo.length ? solo : names.filter((n) => !stripOf(settings, n)?.mute);
};
